import { Router } from "@angular/router";
declare var bootstrap: any;

export class Utilities {
  static readonly PLATFORM = 'web';
  static readonly EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  static readonly ADDRESS_SEARCH_DEBOUNCER_TIME = 600; //milliseconds
  static readonly FILTER_SEARCH_DEBOUNCER_TIME = 0; //milliseconds
  static readonly GUEST_MODE_DISCOUNT_PERCENT = 20; //20% Voucher Discount
  static readonly EMPTY_LOADER_TIMER = 1000;
  static readonly PHONE_REGIX = /(?<!\d)(?:\+44|0044|0)\s?(?:\d\s?){9,10}(?!\d)|(?<!\d)\d[\d\s-]{8,}(?!\d)/g;
  static readonly WORD_NUMBER_REGIX = /\b(?:(?:zero|one|two|three|four|five|six|seven|eight|nine)[\s-]*){7,}\b/gi;
  static readonly BOOKING_LOADER = 1800; // 1.8s
  static readonly BOOKING_LOADER_INITIAL = 1500; // 1.5s
  static readonly UK_NUMBER_PREFIX = +44;
  static readonly STOPS_COMPLETED_STATUS = 'STOP_COMPLETED';
  static DOOR_NUMBER_PLACEHOLDER = 'Door No#/House/Building Name';

  static decodeToken(token: string): any | null {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (e) {
      console.error('Invalid token', e);
      return null;
    }
  }

  static isTokenExpired(token: string, graceSeconds: number = 0): boolean {
    const payload = this.decodeToken(token);
    if (!payload) return true;
    const exp = payload.exp; //by default exp is 30 days - set from backend
    const now = Math.floor(Date.now() / 1000);

    return now > exp;
  }

  static formatStopDisplayTime(stopDisplayTime: string): string {
    if (!stopDisplayTime) return '';
    if (stopDisplayTime === 'ASAP') return stopDisplayTime;

    const [startStr, endStr] = stopDisplayTime.split('–').map((s) => s.trim());

    // Remove BST/GMT/UTC and split into time + date parts
    const [startTimeStr, startDateStr] = startStr
      .replace(/BST|GMT|UTC/g, '')
      .trim()
      .split('|')
      .map((s) => s.trim());
    const [endTimeStr, endDateStr] = endStr
      .replace(/BST|GMT|UTC/g, '')
      .trim()
      .split('|')
      .map((s) => s.trim());

    // Construct full date-time strings (e.g., "09 Sep 2025 13:40")
    const currentYear = new Date().getFullYear();
    const startDateTime = new Date(`${startDateStr} ${currentYear} ${startTimeStr}`);
    const endDateTime = new Date(`${endDateStr} ${currentYear} ${endTimeStr}`);

    // Format parts
    const startDay = startDateTime.toLocaleDateString('en-US', { weekday: 'long' });
    const endDay = endDateTime.toLocaleDateString('en-US', { weekday: 'long' });

    const startTime = startDateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const endTime = endDateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

    // Same date → "Monday between 08:30 AM – 09:30 PM"
    if (startDateStr === undefined && endDateStr) {
      return `${endDay} between ${startTime} – ${endTime}`;
    } else {
      // Different dates → "Monday 08:30 AM to Tuesday 09:30 PM"
      return `${startDay} ${startTime} to ${endDay} ${endTime}`;
    }
  }

  static formatToTimeMonthDay(dateString: string): string {
    //14:25 Oct 10
    if (!dateString) return '-';
    const normalized = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const parsed = Date.parse(normalized);
    if (isNaN(parsed)) return '';
    const date = new Date(parsed);
    // Format parts
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${hours}:${minutes} ${month} ${day}`;
  }

  private static getBackendDateTimeParts(value: unknown): {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
  } | null {
    if (value === null || value === undefined || value === '') return null;

    const rawValue = String(value).trim();
    const backendMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})[T\s](\d{1,2}):(\d{2})/);
    const isoMatch = rawValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[T\s](\d{1,2}):(\d{2})/);

    const parts = backendMatch
      ? {
        year: Number(backendMatch[3]),
        month: Number(backendMatch[1]),
        day: Number(backendMatch[2]),
        hour: Number(backendMatch[4]),
        minute: Number(backendMatch[5]),
      }
      : isoMatch
        ? {
          year: Number(isoMatch[1]),
          month: Number(isoMatch[2]),
          day: Number(isoMatch[3]),
          hour: Number(isoMatch[4]),
          minute: Number(isoMatch[5]),
        }
        : null;

    if (
      !parts ||
      parts.month < 1 || parts.month > 12 ||
      parts.day < 1 || parts.day > 31 ||
      parts.hour < 0 || parts.hour > 23 ||
      parts.minute < 0 || parts.minute > 59
    ) {
      return null;
    }

    return parts;
  }

  static formatBackendTime(value: unknown): string {
    const parts = this.getBackendDateTimeParts(value);
    if (!parts) return '';

    const displayHour = parts.hour % 12 || 12;
    const meridiem = parts.hour >= 12 ? 'pm' : 'am';
    return `${displayHour}:${parts.minute.toString().padStart(2, '0')} ${meridiem}`;
  }

  static formatBackendDayMonth(value: unknown): string {
    const parts = this.getBackendDateTimeParts(value);
    if (!parts) return '';

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${parts.day.toString().padStart(2, '0')} ${months[parts.month - 1]}`;
  }

  static formatBackendDateTime(value: unknown): string {
    const parts = this.getBackendDateTimeParts(value);
    if (!parts) return value == null ? '' : String(value);

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const displayHour = parts.hour % 12 || 12;
    const meridiem = parts.hour >= 12 ? 'PM' : 'AM';
    const minute = parts.minute.toString().padStart(2, '0');
    return `${months[parts.month - 1]} ${parts.day}, ${parts.year}, ${displayHour}:${minute} ${meridiem}`;
  }

  static getVoucherTypeUnit(voucherType: string, value: any): string {
    if (voucherType == 'Percentage') {
      return value + '%';
    } else {
      return '£' + value;
    }
  }

  static orgAddressFilter(res: any): string {
    let address = [res?.address1, res?.address2, [res?.postCode, res?.city].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ');

    return address;
  }

  static onRequote(router: Router, loadId: any) {
    router.navigate(['dashboard', 'request-new-quote'], {
      queryParams: { requote: loadId },
    });
  }

  static getErrorMessage(error: any): string {
    const responseError = error?.error;
    const errorMessage = typeof responseError === 'string'
      ? responseError
      : responseError?.text || responseError?.message || error?.message;

    return errorMessage || 'Something went wrong. Please try again.';
  }

  static getFilteredNotes(note: string): string {
    if (!note) return '';
    const cleanedNote = note
      .replace(/Target Rate for this Load is:\s*\d+(\.\d+)?/g, ' ')
      .replace(/<\s*br\s*\/?\s*>/gi, ' ')
      .replace(/Pick up ASAP\s*-\s*/i, '')
      .replace(/ONLINE VEHICLES ONLY\. ?TRACKABLE VEHICLES PREFERRED\./i, '')
      .replace(/ONLINE QUOTES ONLY\. ?TRACKABLE VEHICLES PREFERRED\./i, '')
      .replace(/\*\*\*.*?\*\*\*/g, '')
      .trim();

    return cleanedNote;
  }

  static phoneNumberRegixUK(phone: string): string {
    if (!phone) return '';
    let cleanedPhone = phone.replace(/\s+/g, '');
    if (cleanedPhone.startsWith('0')) {
      cleanedPhone = Utilities.UK_NUMBER_PREFIX + cleanedPhone.slice(1);
    } else if (cleanedPhone.startsWith('44')) {
      cleanedPhone = '+' + cleanedPhone;
    }
    return cleanedPhone;
  }

  static toUtcZFormat(localDateTimeStr: string): string {
    const localDate = new Date(localDateTimeStr);
    localDate.setSeconds(0, 0);
    const iso = localDate.toISOString();
    const formatted = iso.split('.')[0] + 'Z';
    return formatted;
  }

  static closeConfirmationModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const modalInstance = bootstrap.Modal.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      } else {
        const newModal = new bootstrap.Modal(modalElement);
        newModal.hide();
      }
    }
  }

  static readonly ALL_VEHICLES = [
    {
      name: 'Bicycle',
      icon: '../../../assets/icons/VehicleTypes/Bicycle.png',
      details: {
        length: '45 centimeters',
        width: '35 centimeters',
        height: '35 centimeters',
        maxWeight: '8kg',
        netVolume: '0.05',
      },
      apiKey: 'Bicycle',
    },
    {
      name: 'Cargo Bike',
      icon: '../../../assets/icons/VehicleTypes/CargoBike.png',
      details: {
        length: '200 centimeters',
        width: '90 centimeters',
        height: '12 centimeters',
        maxWeight: '150kg',
        netVolume: '1.5',
      },
      apiKey: 'Cargo Bike',
    },
    {
      name: 'Motorbike',
      icon: '../../../assets/icons/VehicleTypes/Bike.png',
      details: {
        length: '40 centimeters',
        width: '40 centimeters',
        height: '30 centimeters',
        maxWeight: '20kg',
        netVolume: '0.048',
      },
      apiKey: 'Bike',
    },
    {
      name: 'Car',
      icon: '../../../assets/icons/VehicleTypes/Car.png',
      details: {
        length: '1 meters',
        width: '1 meters',
        height: '0.5 meters',

        maxWeight: '50kg',
        netVolume: '0.5',
      },
      apiKey: 'Car',
    },
    {
      name: 'Small Van',
      icon: '../../../assets/icons/VehicleTypes/SVan.png',
      details: {
        length: '1.30 meters',
        width: '1.20 meters',
        height: '1.00 meters',
        pallets: 'Up to 1',
        maxWeight: '400kg',
        netVolume: '1.56',
      },
      apiKey: 'S/Van',
    },
    {
      name: 'Short Wheel Base Van',
      icon: '../../../assets/icons/VehicleTypes/SWB.png',
      details: {
        length: '2.10 meters',
        width: '1.20 meters',
        height: '1.40 meters',
        pallets: 'Up to 1',
        maxWeight: '800kg',
        netVolume: '3.53',
      },
      apiKey: 'SWB',
    },
    {
      name: 'Medium Wheel Base Van',
      icon: '../../../assets/icons/VehicleTypes/Transit.png',
      details: {
        length: '2.20 meters',
        width: '1.20 meters',
        height: '1.40 meters',
        pallets: 'Up to 1',
        maxWeight: '900kg',
        netVolume: '3.69',
      },
      apiKey: 'Transit',
    },
    {
      name: 'Long Wheel Base Van',
      icon: '../../../assets/icons/VehicleTypes/LWB.png',
      details: {
        length: '3.30 meters',
        width: '1.20 meters',
        height: '1.70 meters',
        pallets: 'Up to 3',
        maxWeight: '1000kg',
        netVolume: '6.73',
      },
      apiKey: 'LWB',
    },
    {
      name: 'Extra Long Wheel Base Van',
      icon: '../../../assets/icons/VehicleTypes/XLWB.png',
      details: {
        length: '4.20 meters',
        width: '1.20 meters',
        height: '1.70 meters',
        pallets: 'Up to 4',
        maxWeight: '1200kg',
        netVolume: '8.57',
      },
      apiKey: 'XLWB',
    },
    {
      name: 'Luton Van',
      icon: '../../../assets/icons/VehicleTypes/Luton.png',
      details: {
        length: '4.20 meters',
        width: '2.00 meters',
        height: '2.10 meters',
        pallets: 'Up to 6',
        maxWeight: '1200kg',
        netVolume: '17.64',
      },
      apiKey: 'Luton',
    },
    {
      name: '7.5 Tonne Truck',
      icon: '../../../assets/icons/VehicleTypes/7.5T.png',
      details: {
        length: '6.00 meters',
        width: '2.30 meters',
        height: '2.20 meters',
        pallets: 'Up to 10',
        maxWeight: '2700kg',
        netVolume: '30.36',
      },
      apiKey: '7.5T',
    },
    {
      name: '12 Tonne Truck',
      icon: '../../../assets/icons/VehicleTypes/12T.png',
      details: {
        length: '7.00 meters',
        width: '2.30 meters',
        height: '2.20 meters',
        pallets: 'Up to 12',
        maxWeight: '6000kg',
        netVolume: '35.42',
      },
      apiKey: '12T',
    },
    {
      name: '18 Tonne Truck',
      icon: '../../../assets/icons/VehicleTypes/18T.png',
      details: {
        length: '7.30 meters',
        width: '2.50 meters',
        height: '2.40 meters',
        pallets: 'Up to 14',
        maxWeight: '9000kg',
        netVolume: '43.80',
      },
      apiKey: '18T',
    },
    {
      name: 'Artic',
      icon: '../../../assets/icons/VehicleTypes/26T.png',
      details: {
        length: '13.60 meters',
        width: '2.40 meters',
        height: '2.70 meters',
        pallets: 'Up to 26',
        maxWeight: '25000kg',
        netVolume: '88.13',
      },
      apiKey: '13.6M',
    },
  ];

  static readonly LIVE_AVAILABILITY_VEHICLES = [
    {
      name: 'Bicycle',
      icon: '../../../assets/icons/VehicleMaps/12.svg',
      details: {
        length: '45 centimeters',
        width: '35 centimeters',
        height: '35 centimeters',
        maxWeight: '8kg',
        netVolume: '0.05',
      },
      apiKey: 'Bicycle',
    },
    {
      name: 'Cargo Bike',
      icon: '../../../assets/icons/VehicleMaps/13.png',
      details: {
        length: '200 centimeters',
        width: '90 centimeters',
        height: '12 centimeters',
        maxWeight: '150kg',
        netVolume: '1.5',
      },
      apiKey: 'Cargo Bike',
    },
    {
      name: 'Motorbike',
      icon: '../../../assets/icons/VehicleMaps/0.png',
      details: {
        length: '40 centimeters',
        width: '40 centimeters',
        height: '30 centimeters',
        maxWeight: '20kg',
        netVolume: '0.048',
      },
      apiKey: 'Bike',
    },
    {
      name: 'Car',
      icon: '../../../assets/icons/VehicleMaps/1.png',
      details: {
        length: '1 meters',
        width: '1 meters',
        height: '0.5 meters',
        maxWeight: '400kg',
        netVolume: '0.5',
      },
      apiKey: 'Car',
    },
    {
      name: 'Small Van',
      icon: '../../../assets/icons/VehicleMaps/2.png',
      details: {
        length: '1.30 meters',
        width: '1.20 meters',
        height: '1.00 meters',
        pallets: 'Up to 1',
        maxWeight: '400kg',
        netVolume: '1.56',
      },
      apiKey: 'S/Van',
    },
    {
      name: 'SWB Van',
      icon: '../../../assets/icons/VehicleMaps/3.png',
      details: {
        length: '2.10 meters',
        width: '1.20 meters',
        height: '1.40 meters',
        pallets: 'Up to 1',
        maxWeight: '800kg',
        netVolume: '3.53',
      },
      apiKey: 'SWB',
    },
    {
      name: 'MWB Van',
      icon: '../../../assets/icons/VehicleMaps/4.png',
      details: {
        length: '2.20 meters',
        width: '1.20 meters',
        height: '1.40 meters',
        pallets: 'Up to 1',
        maxWeight: '900kg',
        netVolume: '3.69',
      },
      apiKey: 'Transit',
    },
    {
      name: 'LWB Van',
      icon: '../../../assets/icons/VehicleMaps/5.png',
      details: {
        length: '3.30 meters',
        width: '1.20 meters',
        height: '1.70 meters',
        pallets: 'Up to 3',
        maxWeight: '1000kg',
        netVolume: '6.73',
      },
      apiKey: 'LWB',
    },
    {
      name: 'XLWB Van',
      icon: '../../../assets/icons/VehicleMaps/6.png',
      details: {
        length: '4.20 meters',
        width: '1.20 meters',
        height: '1.70 meters',
        pallets: 'Up to 4',
        maxWeight: '1200kg',
        netVolume: '8.57',
      },
      apiKey: 'XLWB',
    },
    {
      name: 'Luton Van',
      icon: '../../../assets/icons/VehicleMaps/7.png',
      details: {
        length: '4.20 meters',
        width: '2.00 meters',
        height: '2.10 meters',
        pallets: 'Up to 6',
        maxWeight: '1200kg',
        netVolume: '17.64',
      },
      apiKey: 'Luton',
    },
    {
      name: '7.5T Truck',
      icon: '../../../../assets/icons/VehicleMaps/8.png',
      details: {
        length: '6.00 meters',
        width: '2.30 meters',
        height: '2.20 meters',
        pallets: 'Up to 10',
        maxWeight: '2700kg',
        netVolume: '30.36',
      },
      apiKey: '7.5T',
    },
    {
      name: '12T Truck',
      icon: '../../../../assets/icons/VehicleMaps/9.png',
      details: {
        length: '7.00 meters',
        width: '2.30 meters',
        height: '2.20 meters',
        pallets: 'Up to 12',
        maxWeight: '6000kg',
        netVolume: '35.42',
      },
      apiKey: '12T',
    },
    {
      name: '18T Truck',
      icon: '../../../../assets/icons/VehicleMaps/10.png',
      details: {
        length: '7.30 meters',
        width: '2.50 meters',
        height: '2.40 meters',
        pallets: 'Up to 14',
        maxWeight: '9000kg',
        netVolume: '43.80',
      },
      apiKey: '18T',
    },
    {
      name: 'Artic',
      icon: '../../../../assets/icons/VehicleMaps/11.png',
      details: {
        length: '13.60 meters',
        width: '2.40 meters',
        height: '2.70 meters',
        pallets: 'Up to 26',
        maxWeight: '25000kg',
        netVolume: '88.13',
      },
      apiKey: '13.6M',
    },
  ];

  static readonly LIGHT_VEHICLES = [
    {
      name: 'Bicycle',
      icon: '../../../assets/icons/VehicleTypes/Bicycle.png',
      details: {
        length: '45 centimeters',
        width: '35 centimeters',
        height: '35 centimeters',
        maxWeight: '8kg',
        netVolume: '0.05',
      },
      apiKey: 'Bicycle',
    },
    {
      name: 'Cargo Bike',
      icon: '../../../assets/icons/VehicleTypes/CargoBike.png',
      details: {
        length: '200 centimeters',
        width: '90 centimeters',
        height: '12 centimeters',
        maxWeight: '150kg',
        netVolume: '1.5',
      },
      apiKey: 'Cargo Bike',
    },
    {
      name: 'Motorbike',
      icon: '../../../assets/icons/VehicleTypes/Bike.png',
      details: {
        length: '40 centimeters',
        width: '40 centimeters',
        height: '30 centimeters',
        maxWeight: '20kg',
        netVolume: '0.048',
      },
      apiKey: 'Bike',
    },
    {
      name: 'Car',
      icon: '../../../assets/icons/VehicleTypes/Car.png',
      details: {
        length: '1 meters',
        width: '1 meters',
        height: '0.5 meters',

        maxWeight: '50kg',
        netVolume: '0.5',
      },
      apiKey: 'Car',
    },
    {
      name: 'Small Van',
      icon: '../../../assets/icons/VehicleTypes/SVan.png',
      details: {
        length: '1.30 meters',
        width: '1.20 meters',
        height: '1.00 meters',
        pallets: 'Up to 1',
        maxWeight: '400kg',
        netVolume: '1.56',
      },
      apiKey: 'S/Van',
    },
    {
      name: 'Short Wheel Base Van',
      icon: '../../../assets/icons/VehicleTypes/SWB.png',
      details: {
        length: '2.10 meters',
        width: '1.20 meters',
        height: '1.40 meters',
        pallets: 'Up to 1',
        maxWeight: '800kg',
        netVolume: '3.53',
      },
      apiKey: 'SWB',
    },
    {
      name: 'Medium Wheel Base Van',
      icon: '../../../assets/icons/VehicleTypes/Transit.png',
      details: {
        length: '2.20 meters',
        width: '1.20 meters',
        height: '1.40 meters',
        pallets: 'Up to 1',
        maxWeight: '900kg',
        netVolume: '3.69',
      },
      apiKey: 'Transit',
    },
    {
      name: 'Long Wheel Base Van',
      icon: '../../../assets/icons/VehicleTypes/LWB.png',
      details: {
        length: '3.30 meters',
        width: '1.20 meters',
        height: '1.70 meters',
        pallets: 'Up to 3',
        maxWeight: '1000kg',
        netVolume: '6.73',
      },
      apiKey: 'LWB',
    },
    {
      name: 'Extra Long Wheel Base Van',
      icon: '../../../assets/icons/VehicleTypes/XLWB.png',
      details: {
        length: '4.20 meters',
        width: '1.20 meters',
        height: '1.70 meters',
        pallets: 'Up to 4',
        maxWeight: '1200kg',
        netVolume: '8.57',
      },
      apiKey: 'XLWB',
    },
    {
      name: 'Luton Van',
      icon: '../../../assets/icons/VehicleTypes/Luton.png',
      details: {
        length: '4.20 meters',
        width: '2.00 meters',
        height: '2.10 meters',
        pallets: 'Up to 6',
        maxWeight: '1200kg',
        netVolume: '17.64',
      },
      apiKey: 'Luton',
    },
    // {
    //   name: '7.5 Tonne Truck',
    //   icon: '../../../assets/icons/VehicleTypes/7.5T.png',
    //   details: {
    //     length: '6.00 meters',
    //     width: '2.30 meters',
    //     height: '2.20 meters',
    //     pallets: 'Up to 10',
    //     maxWeight: '2700kg',
    //     netVolume: '30.36',
    //   },
    //   apiKey: '7.5T'
    // },
    // {
    //   name: '12 Tonne Truck',
    //   icon: '../../../assets/icons/VehicleTypes/12T.png',
    //   details: {
    //     length: '7.00 meters',
    //     width: '2.30 meters',
    //     height: '2.20 meters',
    //     pallets: 'Up to 12',
    //     maxWeight: '6000kg',
    //     netVolume: '35.42',
    //   },
    //   apiKey: '12T'
    // },
    // {
    //   name: '18 Tonne Truck',
    //   icon: '../../../assets/icons/VehicleTypes/18T.png',
    //   details: {
    //     length: '7.30 meters',
    //     width: '2.50 meters',
    //     height: '2.40 meters',
    //     pallets: 'Up to 14',
    //     maxWeight: '9000kg',
    //     netVolume: '43.80',
    //   },
    //   apiKey: '18T'
    // },
    // {
    //   name: 'Artic',
    //   icon: '../../../assets/icons/VehicleTypes/26T.png',
    //   details: {
    //     length: '13.60 meters',
    //     width: '2.40 meters',
    //     height: '2.70 meters',
    //     pallets: 'Up to 26',
    //     maxWeight: '25000kg',
    //     netVolume: '88.13',
    //   },
    //   apiKey: '13.6M'
    // }
  ];

  static readonly LIVE_TRACKING_VEHICLES = [
    {
      name: 'Bicycle',
      icon: '../../../../assets/icons/VehicleMaps/12.svg',
      apiKey: 'Bicycle',
    },
    {
      name: 'Cargo Bike',
      icon: '../../../../assets/icons/VehicleMaps/13.png',
      apiKey: 'Cargo Bike',
    },
    {
      name: 'Motorbike',
      icon: '../../../../assets/icons/VehicleMaps/0.png',
      apiKey: 'Bike',
    },
    {
      name: 'Car',
      icon: '../../../../assets/icons/VehicleMaps/1.png',
      apiKey: 'Car',
    },
    {
      name: 'Small Van',
      icon: '../../../../assets/icons/VehicleMaps/2.png',
      apiKey: 'S/Van',
    },
    {
      name: 'Midi Van',
      icon: '../../../../assets/icons/VehicleMaps/2.png',
      apiKey: 'M/Van',
    },
    {
      name: 'SWB Van',
      icon: '../../../../assets/icons/VehicleMaps/3.png',
      apiKey: 'SWB',
    },
    {
      name: 'MWB Van',
      icon: '../../../../assets/icons/VehicleMaps/4.png',
      apiKey: 'Transit',
    },
    {
      name: 'LWB Van',
      icon: '../../../../assets/icons/VehicleMaps/5.png',
      apiKey: 'LWB',
    },
    {
      name: 'XLWB Van',
      icon: '../../../../assets/icons/VehicleMaps/6.png',
      apiKey: 'XLWB',
    },
    {
      name: 'Luton Van',
      icon: '../../../../assets/icons/VehicleMaps/7.png',
      apiKey: 'Luton',
    },
  ];
}

export enum VAT {
  INC = 'incl. VAT',
  EXC = 'excl. VAT'
}

export enum OtpAction {
  VERIFY = 'verify',
  LOGIN = 'login'
}

export enum TABS {
  QUOTE = 'Quotes',
  DELIVERY = 'Deliveries'
}

export enum QUOTES_TABS {
  ALL = 'all',
  ACTIVE = 'active',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  BOOKED = 'booked'
}

export enum APP_LINKS {
  APP_STORE = "https://apps.apple.com/gb/app/odda/id6744525658",
  PLAY_STORE = "https://play.google.com/store/apps/details?id=com.intercargo.odda&pli=1",
}

export enum UserRole {
  None = 0,
  Admin = 1,
  User = 2,
  ExecutiveDirector = 3,
  Director = 4,
  GeneralManager = 5,
  FinanceManager = 6,
  TeamLeadIT = 7,
  SoftwareEngineer = 8,
  Controller = 9,
  Dispatcher = 10,
  Sales = 11,
  OperationStaff = 12,
  TeamLeadOperations = 13
}

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.None]: 'None',
  [UserRole.Admin]: 'Admin',
  [UserRole.User]: 'User',
  [UserRole.ExecutiveDirector]: 'Executive Director',
  [UserRole.Director]: 'Director',
  [UserRole.GeneralManager]: 'General Manager',
  [UserRole.FinanceManager]: 'Finance Manager',
  [UserRole.TeamLeadIT]: 'Team Lead IT',
  [UserRole.SoftwareEngineer]: 'Software Engineer',
  [UserRole.Controller]: 'Controller',
  [UserRole.Dispatcher]: 'Dispatcher',
  [UserRole.Sales]: 'Sales',
  [UserRole.OperationStaff]: 'Operation Staff',
  [UserRole.TeamLeadOperations]: 'Team Lead Operations'
};

export interface RoleOption {
  value: number;
  name: string;
  label?: string;
}

export interface UserRolesResponse {
  roles: RoleOption[];
}

export function formatRoleName(rawName?: string): string {
  if (!rawName) return '';
  return rawName
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

export const DYNAMIC_ROLES_CACHE: Record<number, string> = {};

export function updateRoleCache(roles: RoleOption[]): void {
  if (!Array.isArray(roles)) return;
  roles.forEach((r) => {
    if (r && typeof r.value === 'number' && r.name) {
      DYNAMIC_ROLES_CACHE[r.value] = formatRoleName(r.name) || r.name;
    }
  });
}

export function getUserRoleName(role?: number | UserRole | null): string {
  if (role === null || role === undefined) return '';
  const numRole = Number(role);
  if (!Number.isNaN(numRole) && DYNAMIC_ROLES_CACHE[numRole]) {
    return DYNAMIC_ROLES_CACHE[numRole];
  }
  return USER_ROLE_LABELS[role as UserRole] || (typeof role === 'number' ? `Role ${role}` : String(role));
}
